/**
 * Service Worker Utilities
 * Helper functions to manage service worker updates and cache clearing
 */

/**
 * Force update the service worker and clear all caches
 * Useful when users experience issues with stale cached content
 */
export const forceServiceWorkerUpdate = async (): Promise<boolean> => {
  try {
    if (!("serviceWorker" in navigator)) {
      console.log("Service workers not supported");
      return false;
    }

    // Get the service worker registration
    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      console.log("No service worker registration found");
      return false;
    }

    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map((cacheName) => {
        console.log("Deleting cache:", cacheName);
        return caches.delete(cacheName);
      })
    );

    // Unregister the current service worker
    await registration.unregister();
    console.log("Service worker unregistered");

    // Force reload to register new service worker
    window.location.reload();

    return true;
  } catch (error) {
    console.error("Error updating service worker:", error);
    return false;
  }
};

/**
 * Clear all application caches without unregistering the service worker
 */
export const clearAllCaches = async (): Promise<boolean> => {
  try {
    if (!("caches" in window)) {
      console.log("Cache API not supported");
      return false;
    }

    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map((cacheName) => {
        console.log("Clearing cache:", cacheName);
        return caches.delete(cacheName);
      })
    );

    console.log("All caches cleared");
    return true;
  } catch (error) {
    console.error("Error clearing caches:", error);
    return false;
  }
};

/**
 * Check if there's an update available for the service worker
 */
export const checkForServiceWorkerUpdate = async (): Promise<boolean> => {
  try {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return false;
    }

    // Check for update
    await registration.update();

    // Return true if there's a waiting service worker (update available)
    return !!registration.waiting;
  } catch (error) {
    console.error("Error checking for service worker update:", error);
    return false;
  }
};

/**
 * Skip waiting and activate the new service worker immediately
 */
export const activateWaitingServiceWorker = async (): Promise<boolean> => {
  try {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration || !registration.waiting) {
      console.log("No waiting service worker found");
      return false;
    }

    // Tell the waiting service worker to skip waiting
    registration.waiting.postMessage({ type: "SKIP_WAITING" });

    // Reload the page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 100);

    return true;
  } catch (error) {
    console.error("Error activating service worker:", error);
    return false;
  }
};

/**
 * Get service worker status information
 */
export const getServiceWorkerStatus = async (): Promise<{
  registered: boolean;
  active: boolean;
  waiting: boolean;
  installing: boolean;
}> => {
  try {
    if (!("serviceWorker" in navigator)) {
      return {
        registered: false,
        active: false,
        waiting: false,
        installing: false,
      };
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return {
        registered: false,
        active: false,
        waiting: false,
        installing: false,
      };
    }

    return {
      registered: true,
      active: !!registration.active,
      waiting: !!registration.waiting,
      installing: !!registration.installing,
    };
  } catch (error) {
    console.error("Error getting service worker status:", error);
    return {
      registered: false,
      active: false,
      waiting: false,
      installing: false,
    };
  }
};
